import { useCallback } from "react";

import { useDataProvider } from "@refinedev/core";
import type { CrudFilters } from "@refinedev/core";
import { useQueries, useQuery } from "@tanstack/react-query";

import {
  ENRICHMENT_CLASS_SENTINEL,
  ENRICHMENT_ENTITIES,
  type EnrichmentEntity,
  enrichmentContentTypeOf,
  isParkLimitReached,
} from "@/lib/tina/enrichment-jobs";
import type { PipelineItem } from "@/types/tina";

/**
 * The three states a curator can act on for an enrichment job.
 *
 * - `parked` — `status=hitl`, `hitl_reason=enrichment_low_confidence`. The
 *   queue proper: a proposal is waiting for approve / replace / retry.
 * - `gave_up` — the park-limit state. The job closed `done` /
 *   `final_outcome=manual_review` with `metadata.enrichment_result.
 *   park_limit_reached = true`, the entity is still `NeedsReview`, and nothing
 *   anywhere else in IMS points at it.
 * - `curator_review` — `status=hitl`, `hitl_reason=enrichment_curator_review`, on
 *   the dedicated `enrichment_artist_curation` job (artist-field-locks,
 *   2026-09-28): an automatic write disagreed with a curated field.
 */
export type EnrichmentQueueTab = "parked" | "gave_up" | "curator_review";

/**
 * `GET /events`'s `limit` ceiling (`Query(ge=1, le=200)`).
 *
 * The whole tab is one page, so this is the cap on rows a curator can see at
 * once and `truncated` tells them when it bit.
 */
export const ENRICHMENT_PAGE_SIZE = 200;

/** Every enrichment content type, in vocabulary order. */
const ALL_ENRICHMENT_CONTENT_TYPES = ENRICHMENT_ENTITIES.map(
  enrichmentContentTypeOf,
);

/**
 * The `curator_review` tab's one content type — not one of the eleven
 * `enrichment_*` entity flows in `@/lib/tina/content-types`, and deliberately not
 * added there: `enrichment_artist_curation` job rows enrich no *new* entity kind,
 * they hold a proposed write against an artist's own `enrichment_artist` job.
 */
export const CURATOR_REVIEW_CONTENT_TYPE = "enrichment_artist_curation";

/**
 * The `usePersistedState` key `EnrichmentList` reads its active tab from.
 *
 * Exported so a caller outside that page — the dashboard's
 * `EnrichmentBacklogCard` — can pre-select a tab before navigating there, the
 * same way the page remembers its own last tab across visits. Lives here
 * rather than on `list.tsx` itself so pulling it in does not drag the whole
 * page component (and its AutoForm/table imports) into the dashboard bundle.
 */
export const ENRICHMENT_TAB_STORAGE_KEY = "tina-enrichment:tab";

export interface EnrichmentQueueOptions {
  /**
   * Ask for the counts and **not** the rows.
   *
   * The dashboard card renders eleven numbers and no row, but it shared the
   * queue's request — so `/tina/home` pulled a full page of `EventRead`s on
   * every load to divide them into eleven buckets. That page is free today (prod
   * has zero parked rows) and expensive the moment
   * `TINA_ENRICH_HITL_ENABLED` flips: the whole enrichment proposal rides
   * **inline** on `hitl_context.problem`, so a parked row carries its
   * proposal, and 200 of them is a multi-megabyte body on the most-visited page
   * in Tina.
   *
   * Set here, the hook asks `GET /events` for `limit=1` per content type and
   * keeps only the server's `total` — eleven rows instead of two hundred, and the
   * counts stop being the "lower bound past the page cap" the row-derived ones
   * are.
   *
   * **Honoured on `parked` and `curator_review`**, both plain `status=hitl` reads whose
   * server `total` is exact under the WHERE. **Not on `gave_up`**: a give-up is
   * `park_limit_reached`, a jsonb flag with no column to filter on, so the server's
   * `total` for the `gave_up` query counts every `manual_review` row rather than the
   * give-ups among them. Asking for counts-only there falls back to the row page rather
   * than reporting a number that is wrong in the direction of "more work than there is".
   */
  countsOnly?: boolean;
}

export interface EnrichmentQueue {
  /**
   * Every matching job across the selected types, newest first.
   *
   * Ordered by the server — `GET /events` defaults to `created_at_desc`. The
   * client-side re-sort this used to do existed only to interleave eleven pages.
   *
   * Always empty under {@link EnrichmentQueueOptions.countsOnly}: no row page is
   * requested at all.
   */
  rows: PipelineItem[];
  /**
   * `content_type` → how many jobs carry it, for the entity filter's counts and
   * the dashboard card's bars.
   *
   * Two sources, and which one is in play is what {@link truncated} qualifies:
   *
   * - counts-only — the server's own `total` per type, exact whatever the
   *   backlog size, because no page had to be big enough to hold it;
   * - with rows — counted over the rows in hand, because one request cannot ask
   *   Postgres to GROUP BY and the class sentinel returns rows, not a histogram.
   *   Exact while the page is complete, and a **lower bound** once
   *   {@link truncated} is set, which is why both callers qualify the number
   *   when it is.
   *
   * The one exception is a request that named a single type: there the server's
   * own total is the count, exactly as the old per-type fan-out reported it.
   */
  countsByContentType: Record<string, number>;
  /** What the tab badge shows. Server-side and exact on `parked`. */
  total: number;
  /** True when matching rows exist that this page did not reach. */
  truncated: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

/**
 * The enrichment queue — one `GET /events` for the whole job class.
 *
 * **Why one request and not eleven.** This used to fan out over the original
 * nine `enrichment_*` content types (now eleven — MOO-4814 task 17 added
 * `play`/`performance`), because `list_events` hides enrichment rows
 * unless the caller names a `content_type` (`scrape_class_only()` as the *else*
 * branch of that filter, which stays) and `content_type` was a single
 * exact value. Tina has since shipped the missing opt-in: `content_type=`
 * {@link ENRICHMENT_CLASS_SENTINEL} matches all eleven flows in one indexed
 * query, with `total` counted over the same WHERE. The fan-out is gone, and
 * with it the nine requests `/tina/home` paid for the backlog card on load.
 *
 * What the fan-out bought and this does not: a per-type row cap, so eleven
 * types could return 2,200 rows between them where one page returns 200.
 * `truncated` says so, the entity filter narrows back to 200 of one type, and
 * prod has 949 job rows in total — the cap is theoretical at these volumes,
 * not a limit a curator will meet.
 *
 * A caller that wants only the counts gets the eleven requests back, at
 * `limit=1` — see {@link EnrichmentQueueOptions.countsOnly} for why eleven
 * near-empty responses beat one page of inline proposals.
 */
export function useEnrichmentQueue(
  tab: EnrichmentQueueTab,
  entity: EnrichmentEntity | null,
  options: EnrichmentQueueOptions = {},
): EnrichmentQueue {
  const { getList } = useDataProvider()("tina");

  // `curator_review` ignores `entity` entirely: it has exactly one content type,
  // and it is not one of the eleven the entity picker offers.
  const contentType =
    tab === "curator_review"
      ? CURATOR_REVIEW_CONTENT_TYPE
      : entity
        ? enrichmentContentTypeOf(entity)
        : ENRICHMENT_CLASS_SENTINEL;
  const serverCounts =
    (options.countsOnly ?? false) &&
    (tab === "parked" || tab === "curator_review");

  const fetchPage = useCallback(async (): Promise<{
    data: PipelineItem[];
    total: number;
  }> => {
    const response = await getList!({
      resource: "tina-items",
      pagination: {
        currentPage: 1,
        pageSize: ENRICHMENT_PAGE_SIZE,
        mode: "server" as const,
      },
      filters: queueFilters(contentType, tab),
      sorters: [],
    });
    return { data: response.data as PipelineItem[], total: response.total };
  }, [getList, contentType, tab]);

  const query = useQuery({
    queryKey: ["tina", "enrichment-queue", tab, contentType],
    queryFn: fetchPage,
    enabled: !serverCounts,
    // The queue moves on curator actions and worker claims, not by the second.
    // A short stale window keeps a tab flip from refetching while still
    // refreshing when the page is revisited. A resolve invalidates the whole
    // `enrichment-queue` prefix, so the window never hides a curator's own work.
    staleTime: 30_000,
  });

  // Narrowed to the one type when the caller named an entity, or when the tab is
  // `curator_review` (which has only one type to begin with): counting the other
  // ten would be ten requests whose numbers nothing renders.
  const countedTypes =
    tab === "curator_review"
      ? [CURATOR_REVIEW_CONTENT_TYPE]
      : entity
        ? [contentType]
        : ALL_ENRICHMENT_CONTENT_TYPES;
  const counts = useQueries({
    queries: countedTypes.map((type) => ({
      // Under the same prefix as the row page, so one invalidation clears both.
      queryKey: ["tina", "enrichment-queue", "count", type, tab],
      queryFn: async (): Promise<number> => {
        const response = await getList!({
          resource: "tina-items",
          // Tina's floor is `limit=1`, so one row is the cheapest a count gets.
          // What is being read is `total`, which is computed over the WHERE and
          // not over the page.
          pagination: { currentPage: 1, pageSize: 1, mode: "server" as const },
          filters: queueFilters(type, tab),
          sorters: [],
        });
        return response.total;
      },
      enabled: serverCounts,
      staleTime: 30_000,
    })),
  });

  const page = query.data;
  // `gave_up` cannot be expressed as a filter: `park_limit_reached` is a jsonb
  // flag, not a column. Narrow server-side to the outcome, then keep only the
  // rows that carry the flag — before the HITL gate existed `manual_review`
  // was stamped on those too, and they are NOT give-ups.
  const rows = !page
    ? []
    : tab === "gave_up"
      ? page.data.filter(isParkLimitReached)
      : page.data;

  const countsByContentType: Record<string, number> = {};
  if (serverCounts) {
    countedTypes.forEach((type, index) => {
      const total = counts[index]?.data;
      if (typeof total === "number") countsByContentType[type] = total;
    });
  } else {
    for (const row of rows) {
      countsByContentType[row.content_type] =
        (countsByContentType[row.content_type] ?? 0) + 1;
    }
    // A request that named ONE type already has that type's exact count from
    // the server, so narrowing must not make the dropdown number shrink below
    // what it read a moment ago. `gave_up` still counts rows: its server total
    // is the whole `manual_review` outcome, of which the give-ups are a subset.
    if (page && ((entity && tab === "parked") || tab === "curator_review")) {
      countsByContentType[contentType] = page.total;
    }
  }

  const countsTotal = Object.values(countsByContentType).reduce(
    (sum, value) => sum + value,
    0,
  );

  return {
    rows,
    countsByContentType,
    total: serverCounts
      ? countsTotal
      : page
        ? // On `gave_up` the server total counts un-flagged rows too, so
          // reporting it would promise rows the list does not contain.
          tab === "parked" || tab === "curator_review"
          ? page.total
          : rows.length
        : 0,
    // Measured against the PAGE, never against what survives the client-side
    // filter. On `gave_up` the two differ — the server total is over the
    // `manual_review` outcome — and gating on surviving rows is what let a full
    // page yielding no give-ups render "No job has hit the park cap" with no
    // warning at all: the give-ups could all be older than the newest 200 rows
    // the page scanned. An incomplete scan is incomplete whatever it found.
    //
    // Counts-only has nothing to truncate: each number is a server `total`, so
    // there is no page for rows to fall off the end of.
    truncated: serverCounts ? false : !!page && page.total > page.data.length,
    isLoading: query.isLoading || counts.some((c) => c.isLoading),
    isFetching: query.isFetching || counts.some((c) => c.isFetching),
    isError: query.isError || counts.some((c) => c.isError),
  };
}

function queueFilters(
  contentType: string,
  tab: EnrichmentQueueTab,
): CrudFilters {
  const base: CrudFilters = [
    { field: "content_type", operator: "eq", value: contentType },
  ];
  if (tab === "parked" || tab === "curator_review") {
    return [
      ...base,
      { field: "status", operator: "eq", value: "hitl" },
      {
        field: "hitl_reason",
        operator: "eq",
        value:
          tab === "curator_review"
            ? "enrichment_curator_review"
            : "enrichment_low_confidence",
      },
    ];
  }
  return [
    ...base,
    { field: "status", operator: "eq", value: "done" },
    { field: "final_outcome", operator: "eq", value: "manual_review" },
  ];
}
